// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useTheme from '../../../utils/hooks/useTheme';

// Component imports
import { Form, Row, Col, Typography, Select, Button } from 'antd';
import Prompt from '../../UI/Prompt';

// Misc imports
import { useFetchBotsQuery, useUpdateBotMutation } from '../../../store/bots/botsApi';
import { useFetchIntegrationsQuery } from '../../../store/integrations/integrationsApi';
import { getCurrentQueryState } from '../../../store/utils/getCurrentQueryState';
import { API_TAGS } from '../../../constants/api';

const GenericPluginSettingsForm = ({
  form,
  pluginCategory,
  onSuccess,
  onCanSubmitChange,
  onLoadingChange,
  integrationType
}) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { formProps } = useTheme();

  const { data: fetchedBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: bots } = useFetchBotsQuery({ orgId, urlParams: { includePlugins: true } }, { skip: !orgId || tenantId });
  const { data: integrationsData, isFetching: isFetchingIntegrations } = useFetchIntegrationsQuery({ orgId, urlParams: { type: integrationType } }, { skip: !orgId });

  const bot = useMemo(() => fetchedBot || (bots?.items || []).find(bot => bot.id === form.getFieldValue('bot')), [fetchedBot, bots, form.getFieldValue('bot')]);
  const activePlugin = useMemo(() =>
    (bot?.plugins || []).find(
      plugin => plugin.category === pluginCategory
        && plugin.integration?.type === integrationType
    ),
  [bot, pluginCategory, integrationType]
  );

  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation();
  const [canSubmit, setCanSubmit] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);

  const integrationOptions = useMemo(() => {
    return (integrationsData && integrationsData?.items?.map(int => ({ label: int.name, value: int.id }))) ?? [];
  }, [integrationsData]);

  useEffect(() => {
    form.resetFields();
  }, [fetchedBot?.id]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isUpdatingBot);
    }
  }, [isUpdatingBot]);

  const updateCanSubmit = async () => {
    try {
      await form.validateFields(['integrationId']);
      setCanSubmit(true);
      if (onCanSubmitChange) {
        onCanSubmitChange(true);
      }
    } catch (e) {
      const formHasErrors = e.errorFields?.length > 0;
      setCanSubmit(!formHasErrors);
      if (onCanSubmitChange) {
        onCanSubmitChange(!formHasErrors);
      }
    }
  };

  const onSubmit = async (values) => {
    let isSubmitSuccessful = true;

    if (settingsChanged) {
      const { integrationId, ...settingsValues } = values;
      let newPlugins = [...(bot?.plugins || [])];

      const existingPluginIndex = newPlugins.findIndex(
        p => p.category === pluginCategory
          && (p.integration?.type === integrationType || p.integrationId === integrationId)
      );

      const newPluginData = {
        integrationId: integrationId,
        category: pluginCategory,
        settings: settingsValues
      };

      if (existingPluginIndex >= 0) {
        newPlugins[existingPluginIndex] = { ...newPlugins[existingPluginIndex], ...newPluginData };
      } else {
        newPlugins.push(newPluginData);
      }

      const safePlugins = newPlugins.map(p => ({
        integrationId: p.integrationId || p.integration?.id,
        category: p.category,
        settings: p.settings
      }));

      isSubmitSuccessful = await updateBot({
        orgId,
        tenantId: bot?.id,
        data: { plugins: safePlugins },
        resourcesToInvalidate: [API_TAGS.NLP_PIPELINES]
      }).unwrap();
    }

    if (isSubmitSuccessful) {
      setCanSubmit(false);
      setSettingsChanged(false);
      if (onSuccess) {
        onSuccess(values);
      }
    }
  };

  return (
    <Fragment>
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        onValuesChange={(changedValue) => {
          if (!changedValue.bot) {
            setSettingsChanged(true);
            updateCanSubmit();
          }
        }}
        initialValues={{
          integrationId: activePlugin?.integration?.id || null,
          ...activePlugin?.settings
        }}
      >
        <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
          <Col xs={formProps.mainColSpan}>
            <Typography.Text>{t('Integration Configuration')}</Typography.Text>
            <Typography.Paragraph className="fs-sm" type="secondary">
              {t('Select which available integration this agent plugin will use. Changing the integration does not cause downtime for the plugin.')}
            </Typography.Paragraph>
          </Col>
          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              name="integrationId"
              label={t('Select Integration')}
              rules={[{ required: true, message: t('Please select an integration.') }]}
            >
              <Select
                placeholder={t('Select Integration')}
                loading={isFetchingIntegrations}
                options={integrationOptions}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Prompt
        when={canSubmit}
        onConfirm={() => form.submit()}
        okText={t('Save Changes')}
        cancelText={t('Quit without saving')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />

      {!onSuccess && (
        <Row justify="end" className="mt-4">
          <Col>
            <Button type="primary" size="large" onClick={() => form.submit()} disabled={!canSubmit}>
              {t('Save Changes')}
            </Button>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

GenericPluginSettingsForm.propTypes = {
  form: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onCanSubmitChange: PropTypes.func,
  onLoadingChange: PropTypes.func,
  integrationType: PropTypes.string,
  pluginCategory: PropTypes.string.isRequired
};

export default GenericPluginSettingsForm;
